import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Phone, User, Building2, MessageSquare, Loader2 } from 'lucide-react';
interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}
const initialForm: FormState = {
  name: '',
  email: '',
  company: '',
  phone: '',
  message: ''
};
export function CTA() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  }
  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Please enter your name';
    if (!form.email.trim()) next.email = 'Please enter your email';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Please enter a valid email';
    if (!form.company.trim()) next.company = 'Please enter your company';
    if (!form.message.trim()) next.message = 'Please tell us a bit about your project';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    // Simulated submission
    setTimeout(() => {
      setStatus('success');
      setForm(initialForm);
    }, 1200);
  }
  const fieldBase = 'w-full rounded-lg border bg-white px-4 py-3 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition';
  return <section id="contact" className="relative py-24 bg-brand-navy overflow-hidden" data-id="element-60">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-61">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[480px] h-[480px] bg-brand-green/15 blur-[120px] rounded-full" data-id="element-62" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-brand-blue/10 blur-[120px] rounded-full" data-id="element-63" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-64">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start" data-id="element-65">
          {/* Left: Pitch */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5 lg:sticky lg:top-28" data-id="element-66">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-green/15 text-brand-green text-xs font-semibold uppercase tracking-wider mb-5" data-id="element-67">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-68" />
              Book a Consultation
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading leading-tight mb-5" data-id="element-69">
              Ready to transform your legacy applications?
            </h2>

            <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-70">
              Tell us about your project and our engineering experts will get
              back within 1 business day to discuss your modernization strategy.
            </p>
          </motion.div>

          {/* Right: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-7" data-id="element-71">
            <div className="bg-white rounded-2xl shadow-2xl shadow-brand-navy/40 p-6 sm:p-8 lg:p-10" data-id="element-72">
              {status === 'success' ? <div className="text-center py-12" data-id="element-73">
                  <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-5" data-id="element-74">
                    <CheckCircle2 className="w-8 h-8 text-brand-green" data-id="element-75" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 font-heading mb-2" data-id="element-76">
                    Thanks — we'll be in touch!
                  </h3>
                  <p className="text-slate-600 max-w-sm mx-auto mb-6" data-id="element-77">
                    We've received your request and one of our engineering
                    experts will reach out within 1 business day.
                  </p>
                  <button type="button" onClick={() => setStatus('idle')} className="text-sm font-semibold text-brand-green hover:underline" data-id="element-78">
                    Submit another request
                  </button>
                </div> : <form onSubmit={handleSubmit} noValidate data-id="element-79">
                  <h3 className="text-xl font-bold text-slate-900 font-heading mb-1" data-id="element-80">
                    Tell us about your project
                  </h3>
                  <p className="text-sm text-slate-500 mb-6" data-id="element-81">
                    All fields marked with{' '}
                    <span className="text-brand-green font-semibold" data-id="element-82">*</span>{' '}
                    are required.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4" data-id="element-83">
                    <Field label="Full name" required icon={<User className="w-4 h-4" data-id="element-85" />} error={errors.name} data-id="element-84">
                      <input type="text" autoComplete="name" value={form.name} onChange={e => update('name', e.target.value)} placeholder="Jane Doe" className={`${fieldBase} ${errors.name ? 'border-red-300' : 'border-slate-200'}`} data-id="element-86" />
                    </Field>

                    <Field label="Work email" required icon={<Mail className="w-4 h-4" data-id="element-88" />} error={errors.email} data-id="element-87">
                      <input type="email" autoComplete="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="jane@company.com" className={`${fieldBase} ${errors.email ? 'border-red-300' : 'border-slate-200'}`} data-id="element-89" />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4" data-id="element-90">
                    <Field label="Company" required icon={<Building2 className="w-4 h-4" data-id="element-92" />} error={errors.company} data-id="element-91">
                      <input type="text" autoComplete="organization" value={form.company} onChange={e => update('company', e.target.value)} placeholder="Acme Inc." className={`${fieldBase} ${errors.company ? 'border-red-300' : 'border-slate-200'}`} data-id="element-93" />
                    </Field>

                    <Field label="Phone" hint="Optional" icon={<Phone className="w-4 h-4" data-id="element-95" />} data-id="element-94">
                      <input type="tel" autoComplete="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (555) 123-4567" className={`${fieldBase} border-slate-200`} data-id="element-96" />
                    </Field>
                  </div>

                  <Field label="How can we help?" required icon={<MessageSquare className="w-4 h-4" data-id="element-98" />} error={errors.message} data-id="element-97">
                    <textarea rows={4} value={form.message} onChange={e => update('message', e.target.value)} placeholder="Briefly describe the systems you're looking to modernize, your timeline, and any specific challenges..." className={`${fieldBase} resize-none ${errors.message ? 'border-red-300' : 'border-slate-200'}`} data-id="element-99" />
                  </Field>

                  <button type="submit" disabled={status === 'submitting'} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-brand-green text-white font-bold rounded-lg text-base hover:bg-brand-greenHover transition-colors shadow-lg shadow-brand-green/30 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-100">
                    {status === 'submitting' ? <>
                        <Loader2 className="w-5 h-5 animate-spin" data-id="element-101" />
                        Sending...
                      </> : 'Book a Consultation'}
                  </button>

                  <p className="text-xs text-slate-500 mt-4 text-center" data-id="element-102">
                    By submitting this form you agree to be contacted by
                    BetterCode AI.
                  </p>
                </form>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
interface FieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  icon?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}
function Field({
  label,
  required,
  hint,
  icon,
  error,
  children
}: FieldProps) {
  return <label className="block" data-id="element-103">
      <span className="flex items-center justify-between mb-1.5" data-id="element-104">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700" data-id="element-105">
          {icon && <span className="text-slate-400" data-id="element-106">{icon}</span>}
          {label}
          {required && <span className="text-brand-green" data-id="element-107">*</span>}
        </span>
        {hint && <span className="text-xs text-slate-400" data-id="element-108">{hint}</span>}
      </span>
      {children}
      {error && <span className="block text-xs text-red-500 mt-1" data-id="element-109">{error}</span>}
    </label>;
}